import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useVault } from "@/context/VaultContext";
import { COLORS, FOLDER_COLORS, apptTime, groupByDate, monthGrid, noteColor } from "@/lib/calendar";
import { ymd } from "@/lib/util";
import { DayPanel } from "./DayPanel";

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function DayCell({ d, inMonth, entries, selected, today, onClick }) {
  const key = ymd(d);
  return (
    <button type="button" onClick={onClick} data-testid={`calendar-day-${key}`}
      className={`min-w-0 max-w-full overflow-hidden min-h-[64px] sm:min-h-[104px] p-1.5 text-left border-r border-b flex flex-col gap-1 transition-colors ${inMonth ? "" : "bg-muted/40 text-muted-foreground"} ${selected ? "bg-accent" : "hover:bg-muted/60"}`}>
      <span className={`text-xs font-mono h-6 w-6 flex items-center justify-center rounded-full shrink-0 ${key === today ? "bg-foreground text-background font-bold" : ""}`}>{d.getDate()}</span>
      <span className="hidden sm:flex flex-col gap-0.5 min-w-0 w-full">
        {entries.slice(0, 3).map((n) => (
          <span key={n.id} className="block min-w-0 max-w-full truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ background: noteColor(n) }} data-testid={`calendar-badge-${n.id}`}>
            {n.entryType === "Deadline" ? "! " : ""}{apptTime(n) && `${apptTime(n)} `}{n.title || n.entryType || "Note"}
          </span>
        ))}
        {entries.length > 3 && <span className="text-[10px] text-muted-foreground">+{entries.length - 3} more</span>}
      </span>
      <span className="flex sm:hidden gap-0.5 flex-wrap min-w-0 max-w-full">
        {entries.slice(0, 4).map((n) => <span key={n.id} className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: noteColor(n) }} />)}
      </span>
    </button>
  );
}

export default function CalendarView() {
  const { notes, folders, cal, setCal } = useVault();
  const today = ymd(new Date());
  const cursor = { y: cal.y, m: cal.m };
  const selected = cal.selected;
  const setCursor = (fn) => setCal((c) => ({ ...c, ...(typeof fn === "function" ? fn(c) : fn) }));
  const setSelected = (s) => {
    setCal((c) => ({ ...c, selected: s }));
    if (window.innerWidth < 1024) setTimeout(() => document.querySelector('[data-testid="day-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const byDate = useMemo(() => groupByDate(notes), [notes]);
  const days = monthGrid(cursor.y, cursor.m);
  const shift = (k) => setCursor(({ y, m }) => { const d = new Date(y, m + k, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const goToday = () => { const n = new Date(); setCursor({ y: n.getFullYear(), m: n.getMonth() }); setSelected(today); };
  const title = new Date(cursor.y, cursor.m, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  return (
    <div className="w-full max-w-full min-w-0 p-4 sm:p-6 lg:p-10" data-testid="calendar-view">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Timeline & Scheduler</p>
      <div className="flex flex-wrap items-center gap-3 mt-1 min-w-0">
        <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight break-words min-w-0" data-testid="calendar-month-title">{title}</h1>
        <div className="flex items-center gap-1 ml-auto shrink-0">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev-button" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today-button">Today</Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="calendar-next-button" aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground min-w-0" data-testid="calendar-legend">
        {folders.filter((f) => FOLDER_COLORS[f.id]).map((f) => (
          <span key={f.id} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: COLORS[FOLDER_COLORS[f.id]] }} />{f.name}</span>
        ))}
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-6 mt-6 min-w-0 max-w-full">
        <div className="rounded-lg border overflow-hidden min-w-0 max-w-full">
          <div className="grid grid-cols-7 border-b bg-muted/40 min-w-0">
            {WEEK.map((w) => <span key={w} className="py-2 text-center text-[11px] font-semibold uppercase tracking-wider min-w-0 truncate">{w}</span>)}
          </div>
          <div className="grid grid-cols-7 min-w-0">
            {days.map((d) => (
              <DayCell key={ymd(d)} d={d} inMonth={d.getMonth() === cursor.m} entries={byDate[ymd(d)] || []}
                selected={ymd(d) === selected} today={today} onClick={() => setSelected(ymd(d))} />
            ))}
          </div>
        </div>
        <DayPanel date={selected} entries={byDate[selected] || []} />
      </div>
    </div>
  );
}
